import { useEffect, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import RightTimeApi from "@/API";

export type ProctorSignalRMessage = {
  eventType?: string;
  testId?: number;
  code?: string;
  sectionId?: number;
  sectionName?: string;
  formId?: number;
  questionId?: string;
  response?: string;
  time?: string;
  clientTime?: string;
  id?: number;
  submittedAt?: string;
  values?: Array<{ questionId?: string; response?: string }>;
};

export function useProctorSignalR(
  isEnabled: boolean,
  onMessage: (data?: ProctorSignalRMessage) => void,
) {
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  });

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let connection: signalR.HubConnection | null = null;
    let disposed = false;

    async function start() {
      const resp = await RightTimeApi.getAsync("/negotiate/proctor");
      if (!resp?.ok || !resp.jsonData || disposed) {
        console.warn("Proctor SignalR negotiate failed.");
        return;
      }

      const { url, accessToken } = resp.jsonData;
      if (!url) {
        return;
      }

      connection = new signalR.HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => accessToken,
        })
        .withAutomaticReconnect()
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on("realtimeResponse", (data) => {
        onMessageRef.current({ ...data, eventType: data?.eventType ?? "realtime" });
      });
      connection.on("sectionSubmitted", (data) => {
        onMessageRef.current({ ...data, eventType: data?.eventType ?? "sectionSubmitted" });
      });

      try {
        await connection.start();
      } catch (error) {
        console.warn("Proctor SignalR connection error:", error);
      }
    }

    start();

    return () => {
      disposed = true;
      connection?.stop();
    };
  }, [isEnabled]);
}